import apiClient from './client';
import type {
  AudienceType,
  RateCardRates,
  CohortCompensationResponse,
  CohortCompensationConfig,
  CompensationCalculationResponse,
  CoachCompensationResult,
  CompensationAuditEntry,
} from '../../types';

interface ConfigureResponse {
  config: CohortCompensationConfig;
  resolved_rates: RateCardRates | null;
}

export const cohortCompensationApi = {
  /** Get compensation config for a cohort */
  getConfig: async (cohortId: string): Promise<CohortCompensationResponse> => {
    return apiClient.get<CohortCompensationResponse>(
      `/api/cohorts/${cohortId}/compensation`,
    );
  },

  /**
   * Create or update compensation config.
   * MED-910: also persists cohort-level component exclusions (AC1–AC7) and
   * the configurable huddle count (AC0j).
   */
  configure: async (
    cohortId: string,
    audienceType: AudienceType,
    rateOverrides?: Partial<RateCardRates>,
    excludedComponents?: string[],
    huddleCount?: number,
  ): Promise<ConfigureResponse> => {
    return apiClient.put<ConfigureResponse>(
      `/api/cohorts/${cohortId}/compensation/configure`,
      {
        audience_type: audienceType,
        rate_overrides: rateOverrides || {},
        excluded_components: excludedComponents ?? [],
        ...(huddleCount !== undefined ? { huddle_count: huddleCount } : {}),
      },
    );
  },

  /** Confirm compensation — snapshot and lock */
  confirm: async (cohortId: string): Promise<ConfigureResponse> => {
    return apiClient.post<ConfigureResponse>(
      `/api/cohorts/${cohortId}/compensation/confirm`,
    );
  },

  /** Unlock a confirmed compensation config */
  unlock: async (cohortId: string): Promise<ConfigureResponse> => {
    return apiClient.post<ConfigureResponse>(
      `/api/cohorts/${cohortId}/compensation/unlock`,
    );
  },

  // ============================================================
  // E13-3: Calculation Results & Status Tracking
  // ============================================================

  /** Get calculation results for all coaches in a cohort */
  getCalculation: async (
    cohortId: string,
  ): Promise<CompensationCalculationResponse> => {
    return apiClient.get<CompensationCalculationResponse>(
      `/api/cohorts/${cohortId}/compensation/calculation`,
    );
  },

  /** Get a single coach's detailed compensation breakdown */
  getCoachCalculation: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<CoachCompensationResult> => {
    return apiClient.get<CoachCompensationResult>(
      `/api/cohorts/${cohortId}/compensation/calculation/coach/${coachUserId}`,
    );
  },

  /** Confirm a single coach's compensation */
  confirmCoachResult: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<CoachCompensationResult> => {
    return apiClient.post<CoachCompensationResult>(
      `/api/cohorts/${cohortId}/compensation/calculation/coach/${coachUserId}/confirm`,
    );
  },

  /**
   * Adjust line items and confirm.
   * MED-910 AC8–AC12: per-coach exclusions are saved in the same action.
   */
  adjustAndConfirmCoachResult: async (
    cohortId: string,
    coachUserId: string,
    adjustments: { line_item_id: string; adjusted_total_cents: number }[],
    excludedComponents?: string[],
  ): Promise<CoachCompensationResult> => {
    return apiClient.post<CoachCompensationResult>(
      `/api/cohorts/${cohortId}/compensation/calculation/coach/${coachUserId}/adjust-confirm`,
      { adjustments, excluded_components: excludedComponents ?? [] },
    );
  },

  /** Bulk confirm multiple coach results */
  bulkConfirmResults: async (
    cohortId: string,
    resultIds: string[],
  ): Promise<{ confirmed_count: number }> => {
    return apiClient.post<{ confirmed_count: number }>(
      `/api/cohorts/${cohortId}/compensation/calculation/bulk-confirm`,
      { result_ids: resultIds },
    );
  },

  /** Get audit log for a coach's compensation */
  getAuditLog: async (
    cohortId: string,
    coachUserId: string,
  ): Promise<CompensationAuditEntry[]> => {
    return apiClient.get<CompensationAuditEntry[]>(
      `/api/cohorts/${cohortId}/compensation/calculation/coach/${coachUserId}/audit`,
    );
  },

  /** Export compensation as CSV and trigger browser download (E13-4 AC14) */
  exportCsv: async (cohortId: string): Promise<void> => {
    await apiClient.downloadFile(
      `/api/cohorts/${cohortId}/compensation/calculation/export-csv`,
      `compensation-${cohortId}.csv`,
    );
  },

  // ============================================================
  // E13-1a: Compensation Overrides (MED-663)
  // ============================================================

  /** Set or reset a per-coach compensation override for availability emails */
  setCompensationOverride: async (
    cohortId: string,
    coachUserId: string,
    overrideCents: number | null,
  ): Promise<{ override_cents: number | null }> => {
    return apiClient.put<{ override_cents: number | null }>(
      `/api/cohorts/${cohortId}/compensation/coach/${coachUserId}/override`,
      { override_cents: overrideCents },
    );
  },
};

export default cohortCompensationApi;
